import { useEffect, useState } from 'react';
import { Layout } from '@/components/Layout';
import { 
  Plus, 
  UserCheck, 
  Eye, 
  Edit, 
  Trash2, 
  Shield, 
  ShieldCheck, 
  ShieldAlert,
  Search,
  Users,
  Tags,
  Car,
  Ticket,
  AlertTriangle,
  Settings,
  QrCode,
  Megaphone,
  History,
  CheckCircle2,
  XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import {
  api,
  departmentEndpoints,
  fetchTagBatches,
  getApiErrorMessage,
  objectTypeEndpoints,
  permissionEndpoints,
  resourceEndpoints,
  userEndpoints
} from '@/lib/api';
import { PERMISSIONS } from '@/lib/permissions';

const collectionPayload = (payload, key) => {
  const source = payload?.data ?? payload;
  if (Array.isArray(source)) return source;
  if (Array.isArray(source?.data)) return source.data;
  if (Array.isArray(source?.[key])) return source[key];
  return [];
};

const unwrapAttributes = (source = {}) => {
  const value = source?.data && typeof source.data === 'object' && !Array.isArray(source.data)
    ? source.data
    : source;
  return value?.attributes && typeof value.attributes === 'object' && !Array.isArray(value.attributes)
    ? {...value, ...value.attributes}
    : value;
};

const objectTypeIdFrom = (...sources) => {
  for (const source of sources) {
    const item = unwrapAttributes(source || {});
    const id = item?.object_type_id ?? item?.company_object_type_id ?? item?.gest_object_type_id ?? item?.type_id ?? item?.id;
    if (id !== undefined && id !== null && id !== '') return id;
  }
  return null;
};

const objectTypeLabelFrom = (objectType, fallbackId = null) => {
  const item = unwrapAttributes(objectType || {});
  return item?.name || item?.label || item?.title || (fallbackId ? `Tipo ${fallbackId}` : '');
};

const normalizeObjectTypes = (payload) => {
  const objectTypes = collectionPayload(payload, 'object_types');
  const types = objectTypes.length ? objectTypes : collectionPayload(payload, 'types');
  return types.map((source, index) => {
    const objectType = unwrapAttributes(source);
    const id = objectTypeIdFrom(objectType) ?? objectType.slug ?? index + 1;
    return {
      ...objectType,
      id: String(id),
      name: objectTypeLabelFrom(objectType, id),
    };
  }).filter(objectType => objectType.id && objectType.name);
};

const arrayFrom = (value) => {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return value.split(',').map(item => item.trim()).filter(Boolean);
    }
  }
  return [];
};

const keyFrom = (source) =>
  String(source?.name || source?.slug || source?.key || source?.id || '').trim();

const roleKeys = (roles) =>
  arrayFrom(roles).map(role => (typeof role === 'string' ? role : keyFrom(role))).filter(Boolean);

const roleLabelFrom = (role) => {
  if (!role) return '';
  if (typeof role === 'string') return role;
  return role.label || role.display_name || role.displayName || role.title || role.name || role.slug || '';
};

const humanizePermissionName = (value = '') =>
  value
    .replace(/^gest_/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());

const permissionKeys = (permissions) =>
  arrayFrom(permissions).map(permission => (
    typeof permission === 'string' ? permission : keyFrom(permission)
  )).filter(Boolean);

const splitName = (name = '') => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {firstName: '', lastName: ''};
  if (parts.length === 1) return {firstName: parts[0], lastName: parts[0]};
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

const permissionLabel = (permission, key) =>
  permission?.label || permission?.display_name || permission?.displayName || permission?.title || humanizePermissionName(key);

const normalizePermissions = (payload) =>
  collectionPayload(payload, 'permissions').reduce((acc, permission) => {
    const key = keyFrom(permission);
    if (!key) return acc;
    acc.push({
      id: key,
      label: permissionLabel(permission, key),
      desc: permission?.description || permission?.desc || '',
      raw: permission,
    });
    return acc;
  }, []);

const idList = (...values) =>
  values.flatMap(arrayFrom).map(item => (
    typeof item === 'object'
      ? item?.id ?? item?.resource_id ?? item?.department_id ?? item?.company_department_id
      : item
  )).filter(value => value !== undefined && value !== null && value !== '').map(String);

const accessTypeFromScope = (departments, resources) => {
  if (resources.length > 0) return 'individual';
  if (departments.length > 0) return 'category';
  return 'total';
};

const normalizeUser = (user = {}, index = 0) => {
  const attributes = user?.attributes && typeof user.attributes === 'object' && !Array.isArray(user.attributes)
    ? user.attributes
    : {};
  const source = {...user, ...attributes};
  const nameParts = splitName(source.name ?? source.full_name ?? source.display_name ?? source.email ?? `Utilizador ${index + 1}`);
  const firstName = source.first_name ?? source.firstName ?? nameParts.firstName;
  const lastName = source.last_name ?? source.lastName ?? nameParts.lastName;
  const derivedName = `${firstName} ${lastName}`.trim();
  const departments = idList(source.permission_departments, source.permissionDepartments, source.departments);
  const resources = idList(source.permission_resources, source.permissionResources, source.resources);
  const permissions = permissionKeys(source.permissions);

  return {
    ...source,
    id: source.id ?? source.user_id ?? source.company_user_id ?? source.uuid ?? index + 1,
    uuid: source.uuid,
    first_name: firstName,
    firstName,
    last_name: lastName,
    lastName,
    name: source.name ?? source.full_name ?? source.display_name ?? derivedName ?? source.email ?? `Utilizador ${index + 1}`,
    nickname: source.nickname ?? source.nick_name ?? '',
    email: source.email ?? '',
    phone: source.phone ?? source.phone_number ?? '',
    status: source.status ?? 'active',
    role: source.job_title || source.role_label || roleLabelFrom(source.role) || roleKeys(source.roles)[0] || 'Gestor',
    roles: roleKeys(source.roles),
    accessType: accessTypeFromScope(departments, resources),
    permissions,
    permissionKeys: permissions,
    categories: departments,
    tags: resources,
    createdAt: source.createdAt ?? (source.created_at ? String(source.created_at).split('T')[0] : ''),
    lastAccess: source.lastAccess ?? source.last_access_at ?? source.last_login_at ?? source.updated_at ?? '-',
  };
};

const normalizeUsers = (payload) =>
  collectionPayload(payload, 'users').map(normalizeUser).filter(user => user.id);

const normalizeUserMutationResponse = (payload, fallback) => {
  const source = payload?.data?.user ?? payload?.user ?? payload?.data ?? payload;
  if (!source || Array.isArray(source) || typeof source !== 'object' || (!source.id && !source.user_id && !source.uuid)) {
    return fallback;
  }
  return normalizeUser(source, 0);
};

const normalizeCategories = (payload) =>
  collectionPayload(payload, 'departments')
    .map((department, index) => {
      const attributes = department?.attributes && typeof department.attributes === 'object' && !Array.isArray(department.attributes)
        ? department.attributes
        : {};
      const source = {...department, ...attributes};
      return {
        id: String(source.id ?? source.department_id ?? source.company_department_id ?? source.uuid ?? index + 1),
        name: source.name ?? source.title ?? source.label ?? `Categoria ${index + 1}`,
        count: Number(source.resources_count ?? source.tags_count ?? source.totalTags ?? source.total_tags ?? 0),
      };
    })
    .filter(category => category.id && category.name);

const batchKeyFrom = (batch = {}) =>
  String(batch.id ?? batch.batch_id ?? batch.tag_batch_id ?? batch.gest_tag_batch_id ?? batch.gest_tag_batches_id ?? '').trim();

const buildBatchLookup = (tagBatches = []) => {
  const lookup = new Map();
  tagBatches.forEach(batch => {
    [
      batch.id,
      batch.batch_id,
      batch.tag_batch_id,
      batch.gest_tag_batch_id,
      batch.gest_tag_batches_id,
      batch.code,
      batch.reference,
    ].filter(value => value !== undefined && value !== null && value !== '').forEach(value => {
      lookup.set(String(value), batch);
    });
  });
  return lookup;
};

const normalizeResourceTag = (source = {}, index = 0, context = {}) => {
  const item = unwrapAttributes(source);
  const department = unwrapAttributes(item.company_department ?? item.department ?? {});
  const tag = unwrapAttributes(item.tag ?? item.gest_tag ?? {});
  const embeddedBatch = unwrapAttributes(tag.gest_tag_batch ?? tag.tag_batch ?? tag.batch ?? item.gest_tag_batch ?? item.tag_batch ?? item.batch ?? {});
  const batchId =
    tag.gest_tag_batch_id ??
    tag.gest_tag_batches_id ??
    tag.tag_batch_id ??
    tag.batch_id ??
    item.gest_tag_batch_id ??
    item.gest_tag_batches_id ??
    item.tag_batch_id ??
    item.batch_id ??
    batchKeyFrom(embeddedBatch);
  const batch = context.batchById?.get(String(batchId)) ?? embeddedBatch;
  const objectTypeId = objectTypeIdFrom(
    item.object_type,
    tag.object_type,
    embeddedBatch.object_type,
    batch?.object_type,
    {
      object_type_id: item.object_type_id ?? tag.object_type_id ?? embeddedBatch.object_type_id ?? batch?.objectTypeId ?? batch?.object_type_id,
      company_object_type_id: item.company_object_type_id ?? tag.company_object_type_id ?? embeddedBatch.company_object_type_id ?? batch?.company_object_type_id,
      gest_object_type_id: item.gest_object_type_id ?? tag.gest_object_type_id ?? embeddedBatch.gest_object_type_id ?? batch?.gest_object_type_id,
      type_id: item.type_id ?? tag.type_id ?? embeddedBatch.type_id ?? batch?.type_id,
    }
  );
  const objectType = context.objectTypeById?.get(String(objectTypeId));
  const objectTypeName =
    objectTypeLabelFrom(objectType) ||
    objectTypeLabelFrom(item.object_type) ||
    objectTypeLabelFrom(tag.object_type) ||
    objectTypeLabelFrom(embeddedBatch.object_type) ||
    objectTypeLabelFrom(batch?.object_type) ||
    batch?.objectTypeName ||
    embeddedBatch.object_type_name ||
    batch?.object_type_name ||
    (objectTypeId ? `Tipo ${objectTypeId}` : '');
  const id = item.id ?? item.resource_id ?? tag.id ?? tag.tag_id ?? item.code ?? item.reference ?? index + 1;

  return {
    id: String(id),
    tagCode: String(tag.reference ?? tag.serial_no ?? tag.code ?? tag.id ?? tag.tag_id ?? item.tag_id ?? item.tag_code ?? item.reference ?? item.code ?? id),
    category: objectTypeName || department.name || item.company_department_name || item.department_name || item.category || 'Sem tipo de objeto',
    vehicle: item.reference ?? item.name ?? item.title ?? `Recurso ${index + 1}`,
  };
};

const normalizeResourceTags = (resourcesPayload, tagBatches = [], objectTypes = []) => {
  const batchById = buildBatchLookup(tagBatches);
  const objectTypeById = new Map(objectTypes.map(objectType => [String(objectType.id), objectType]));
  const context = {batchById, objectTypeById};
  return (
  collectionPayload(resourcesPayload, 'resources')
    .map((resource, index) => normalizeResourceTag(resource, index, context))
    .filter(tag => tag.id)
  );
};

const managerPayload = (manager, form, availablePermissions = []) => {
  const availablePermissionIds = new Set(availablePermissions.map(permission => permission.id));
  const permissionNames = Array.from(new Set(
    (form.permissionKeys || [])
      .filter(permission => availablePermissionIds.size === 0 || availablePermissionIds.has(permission))
  ));
  const roles = Array.from(new Set([...(manager.roles || []), 'gestor']));
  const nameParts = splitName(manager.name ?? manager.email ?? '');
  const firstName = manager.first_name ?? manager.firstName ?? nameParts.firstName;
  const lastName = manager.last_name ?? manager.lastName ?? nameParts.lastName;

  return {
    id: manager.id,
    user_id: manager.id,
    uuid: manager.uuid,
    first_name: firstName,
    last_name: lastName,
    name: manager.name,
    nickname: manager.nickname || null,
    email: manager.email,
    phone: manager.phone || null,
    status: manager.status || 'active',
    role: form.role,
    job_title: form.role,
    roles,
    custom_permissions: true,
    permissions: permissionNames,
    permission_departments: form.accessType === 'category' ? form.categories : [],
    permission_resources: form.accessType === 'individual' ? form.tags : [],
  };
};

const managerRemovalPayload = (manager) => {
  const nextRoles = (manager.roles || []).filter(role => role !== 'gestor');
  const nameParts = splitName(manager.name ?? manager.email ?? '');
  const firstName = manager.first_name ?? manager.firstName ?? nameParts.firstName;
  const lastName = manager.last_name ?? manager.lastName ?? nameParts.lastName;

  return {
    id: manager.id,
    user_id: manager.id,
    uuid: manager.uuid,
    first_name: firstName,
    last_name: lastName,
    name: manager.name,
    nickname: manager.nickname || null,
    email: manager.email,
    phone: manager.phone || null,
    status: manager.status || 'active',
    role: nextRoles[0] || '',
    roles: nextRoles,
    custom_permissions: false,
    permissions: [],
    permission_departments: [],
    permission_resources: [],
  };
};

// Fallback data used only if optional API resources fail.
const fallbackUsers = [
  { id: 10, name: 'António Ferreira', email: 'antonio.ferreira@fleet.com' },
  { id: 11, name: 'Sofia Rodrigues', email: 'sofia.rodrigues@fleet.com' },
  { id: 12, name: 'Rui Oliveira', email: 'rui.oliveira@fleet.com' },
  { id: 13, name: 'Ana Costa', email: 'ana.costa@fleet.com' },
];

const accessTypeConfig = {
  total: { label: 'Acesso Total', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ShieldCheck },
  category: { label: 'Por Categoria', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Shield },
  individual: { label: 'Individual', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: ShieldAlert },
};

export const PermissionsPage = () => {
  const [managers, setManagers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState(fallbackUsers);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [permissionOptions, setPermissionOptions] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccessType, setFilterAccessType] = useState('all');
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);
  
  // Form states
  const [formUserId, setFormUserId] = useState('');
  const [formRole, setFormRole] = useState('');
  const [formAccessType, setFormAccessType] = useState('total');
  const [formPermissionKeys, setFormPermissionKeys] = useState([]);
  const [formCategories, setFormCategories] = useState([]);
  const [formTags, setFormTags] = useState([]);
  const {permissions: currentPermissions} = usePermissions();
  const canReadPermissions = currentPermissions.includes(PERMISSIONS.PERMISSION_READ);
  const canCreatePermissions = currentPermissions.includes(PERMISSIONS.PERMISSION_CREATE);
  const canUpdatePermissions = currentPermissions.includes(PERMISSIONS.PERMISSION_UPDATE);
  const canDeletePermissions = currentPermissions.includes(PERMISSIONS.PERMISSION_DELETE);
  const canUsePermissionActions = canUpdatePermissions || canDeletePermissions;

  useEffect(() => {
    let cancelled = false;

    const loadPermissionsPage = async () => {
      if (!canReadPermissions) {
        setPageLoading(false);
        setPageError('N\u00e3o tem permiss\u00e3o para consultar permiss\u00f5es.');
        return;
      }

      setPageLoading(true);
      setPageError('');

      try {
        const [usersResponse, departmentsResponse, resourcesResponse, permissionsResponse, objectTypesResponse, tagBatchesResponse] = await Promise.all([
          api.get(userEndpoints.list),
          api.get(departmentEndpoints.list),
          api.get(resourceEndpoints.list),
          api.get(permissionEndpoints.list).catch(() => ({data: []})),
          api.get(objectTypeEndpoints.list).catch(() => ({data: []})),
          fetchTagBatches().catch(() => []),
        ]);

        const users = normalizeUsers(usersResponse.data);
        const nextManagers = users.filter(user =>
          user.roles.includes('gestor') ||
          user.roles.includes('admin') ||
          user.roles.includes('superadmin') ||
          user.permissionKeys.length > 0 ||
          user.categories.length > 0 ||
          user.tags.length > 0
        );
        const managerIds = new Set(nextManagers.map(user => String(user.id)));
        const nextAvailableUsers = users
          .filter(user => !managerIds.has(String(user.id)))
          .map(user => ({id: user.id, name: user.name, email: user.email, raw: user}));

        if (!cancelled) {
          setManagers(nextManagers);
          setAvailableUsers(users.length ? nextAvailableUsers : fallbackUsers);
          const categories = normalizeCategories(departmentsResponse.data);
          const objectTypes = normalizeObjectTypes(objectTypesResponse.data);
          const tags = normalizeResourceTags(resourcesResponse.data, tagBatchesResponse, objectTypes);
          setAvailableCategories(categories);
          setAvailableTags(tags);
          setPermissionOptions(normalizePermissions(permissionsResponse.data));
        }
      } catch (error) {
        if (!cancelled) {
          setManagers([]);
          setPageError(getApiErrorMessage(error, 'Não foi possível carregar as permissões.'));
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    loadPermissionsPage();

    return () => {
      cancelled = true;
    };
  }, [canReadPermissions]);

  // Reset form
  const resetForm = () => {
    setFormUserId('');
    setFormRole('');
    setFormAccessType('total');
    setFormPermissionKeys([]);
    setFormCategories([]);
    setFormTags([]);
  };

  const denyPermissionAction = () => {
    toast.error('N\u00e3o tem permiss\u00e3o para executar esta a\u00e7\u00e3o.');
  };

  const handleOpenCreate = () => {
    if (!canCreatePermissions) {
      denyPermissionAction();
      return;
    }

    resetForm();
    setIsCreateDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (manager) => {
    if (!canUpdatePermissions) {
      denyPermissionAction();
      return;
    }

    setSelectedManager(manager);
    setFormRole(manager.role);
    setFormAccessType(manager.accessType);
    setFormPermissionKeys([...(manager.permissionKeys || [])]);
    setFormCategories(manager.categories);
    setFormTags(manager.tags);
    setIsEditDialogOpen(true);
  };

  // Open delete dialog
  const handleOpenDelete = (manager) => {
    if (!canDeletePermissions) {
      denyPermissionAction();
      return;
    }

    setSelectedManager(manager);
    setIsDeleteDialogOpen(true);
  };

  const togglePermission = (permissionId) => {
    setFormPermissionKeys(prev =>
      prev.includes(permissionId)
        ? prev.filter(permission => permission !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleAccessTypeChange = (accessType) => {
    setFormAccessType(accessType);

    if (accessType === 'total') {
      setFormCategories([]);
      setFormTags([]);
      return;
    }

    if (accessType === 'category') {
      setFormTags([]);
      return;
    }

    if (accessType === 'individual') {
      setFormCategories([]);
    }
  };

  // Toggle category
  const toggleCategory = (categoryId) => {
    setFormCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Toggle tag
  const toggleTag = (tagId) => {
    setFormTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  // Create manager
  const handleCreate = async () => {
    if (!canCreatePermissions) {
      denyPermissionAction();
      return;
    }

    const user = availableUsers.find(u => String(u.id) === String(formUserId));
    if (!user) {
      toast.error('Selecione um utilizador');
      return;
    }
    if (!formRole) {
      toast.error('Preencha o cargo');
      return;
    }

    const fallbackManager = {
      ...(user.raw || {}),
      id: user.id,
      name: user.name,
      email: user.email,
      role: formRole,
      accessType: formAccessType,
      permissions: formPermissionKeys,
      permissionKeys: formPermissionKeys,
      categories: formCategories,
      tags: formTags,
      createdAt: new Date().toISOString().split('T')[0],
      lastAccess: '-'
    };

    setSubmitting(true);
    try {
      const response = await api.post(userEndpoints.update, managerPayload(fallbackManager, {
        role: formRole,
        accessType: formAccessType,
        permissionKeys: formPermissionKeys,
        categories: formCategories,
        tags: formTags,
      }, permissionOptions));
      const newManager = normalizeUserMutationResponse(response.data, fallbackManager);
      setManagers(prev => [newManager, ...prev]);
      setAvailableUsers(prev => prev.filter(item => String(item.id) !== String(user.id)));
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success(`Gestor ${newManager.name} adicionado com sucesso!`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível adicionar o gestor.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Update manager
  const handleUpdate = async () => {
    if (!selectedManager) return;
    if (!canUpdatePermissions) {
      denyPermissionAction();
      return;
    }

    if (!formRole) {
      toast.error('Preencha o cargo');
      return;
    }

    const fallbackManager = {
      ...selectedManager,
      role: formRole,
      accessType: formAccessType,
      permissions: formPermissionKeys,
      permissionKeys: formPermissionKeys,
      categories: formCategories,
      tags: formTags
    };

    setSubmitting(true);
    try {
      const response = await api.post(userEndpoints.update, managerPayload(selectedManager, {
        role: formRole,
        accessType: formAccessType,
        permissionKeys: formPermissionKeys,
        categories: formCategories,
        tags: formTags,
      }, permissionOptions));
      const updatedManager = normalizeUserMutationResponse(response.data, fallbackManager);
      setManagers(prev => prev.map(m => m.id === selectedManager.id ? {...fallbackManager, ...updatedManager} : m));
      setIsEditDialogOpen(false);
      setSelectedManager(null);
      toast.success('Permissões atualizadas com sucesso!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível atualizar as permissões.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Delete manager
  const handleDelete = async () => {
    if (!selectedManager) return;
    if (!canDeletePermissions) {
      denyPermissionAction();
      return;
    }

    const payload = managerRemovalPayload(selectedManager);
    setSubmitting(true);
    try {
      const response = await api.post(userEndpoints.update, payload);
      const updatedUser = normalizeUserMutationResponse(response.data, {
        ...selectedManager,
        roles: payload.roles,
        permissions: [],
        permissionKeys: [],
        categories: [],
        tags: [],
      });
      setManagers(prev => prev.filter(m => m.id !== selectedManager.id));
      setAvailableUsers(prev => [{id: updatedUser.id, name: updatedUser.name, email: updatedUser.email, raw: updatedUser}, ...prev]);
      setIsDeleteDialogOpen(false);
      setSelectedManager(null);
      toast.success('Gestor removido do sistema');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Não foi possível remover o gestor.'));
    } finally {
      setSubmitting(false);
    }
  };

  // Filter managers
  const filteredManagers = managers.filter(manager => {
    const matchesSearch = manager.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         manager.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         manager.role.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterAccessType === 'all' || manager.accessType === filterAccessType;
    return matchesSearch && matchesType;
  });

  // Stats
  const totalManagers = managers.length;
  const totalAccessManagers = managers.filter(m => m.accessType === 'total').length;
  const categoryAccessManagers = managers.filter(m => m.accessType === 'category').length;
  const individualAccessManagers = managers.filter(m => m.accessType === 'individual').length;

  // Permission form component
  const PermissionForm = ({ isEdit = false }) => (
    <div className="space-y-6">
      {/* User Selection (only for create) */}
      {!isEdit && (
        <div className="space-y-2">
          <Label>Utilizador *</Label>
          <Select value={formUserId} onValueChange={setFormUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Selecionar utilizador" />
            </SelectTrigger>
            <SelectContent>
              {availableUsers.map(user => (
                <SelectItem key={user.id} value={user.id.toString()}>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">
                        {user.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium">{user.name}</span>
                      <span className="text-slate-500 ml-2 text-sm">{user.email}</span>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Role */}
      <div className="space-y-2">
        <Label>Cargo/Função *</Label>
        <Input 
          placeholder="Ex: Gestor de Frota, Supervisor, Operador"
          value={formRole}
          onChange={(e) => setFormRole(e.target.value)}
        />
      </div>

      {/* Access Type */}
      <div className="space-y-3">
        <Label>Tipo de Acesso</Label>
        <div className="grid grid-cols-3 gap-3">
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formAccessType === 'total' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}
            onClick={() => handleAccessTypeChange('total')}
          >
            <ShieldCheck className={`mb-2 ${formAccessType === 'total' ? 'text-emerald-600' : 'text-slate-400'}`} size={24} />
            <p className="font-medium text-slate-900">Acesso Total</p>
            <p className="text-xs text-slate-600">Todas as categorias e tags</p>
          </div>
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formAccessType === 'category' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
            onClick={() => handleAccessTypeChange('category')}
          >
            <Shield className={`mb-2 ${formAccessType === 'category' ? 'text-blue-600' : 'text-slate-400'}`} size={24} />
            <p className="font-medium text-slate-900">Por Categoria</p>
            <p className="text-xs text-slate-600">Categorias específicas</p>
          </div>
          <div 
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${formAccessType === 'individual' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}
            onClick={() => handleAccessTypeChange('individual')}
          >
            <ShieldAlert className={`mb-2 ${formAccessType === 'individual' ? 'text-amber-600' : 'text-slate-400'}`} size={24} />
            <p className="font-medium text-slate-900">Individual</p>
            <p className="text-xs text-slate-600">Tags específicas</p>
          </div>
        </div>
      </div>

      {/* Permissions */}
      <div className="space-y-3">
        <Label>Permissões de Dados</Label>
        {permissionOptions.length === 0 ? (
          <p className="text-sm text-slate-500 rounded-lg border border-slate-200 bg-slate-50 p-3">
            Nenhuma permissão disponível na API.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {permissionOptions.map(permission => {
              const isSelected = formPermissionKeys.includes(permission.id);
              return (
                <div
                  key={permission.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => togglePermission(permission.id)}
                >
                  <div className="flex items-start gap-2">
                    <Checkbox checked={isSelected} />
                    <div className="min-w-0">
                      <p className={`text-sm ${isSelected ? 'text-blue-700 font-medium' : 'text-slate-700'}`}>
                        {permission.label}
                      </p>
                      <p className="text-xs text-slate-500 font-mono truncate">{permission.id}</p>
                      {permission.desc && <p className="text-xs text-slate-500 mt-1">{permission.desc}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Category Selection (if category access) */}
      {formAccessType === 'category' && (
        <div className="space-y-3">
          <Label>Categorias/Filiais Permitidas</Label>
          {availableCategories.length === 0 ? (
            <p className="text-sm text-slate-500 rounded-lg border border-slate-200 bg-slate-50 p-3">
              Nenhuma categoria/filial disponível em /departments.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {availableCategories.map(category => {
                const isSelected = formCategories.includes(category.id);
                return (
                  <div
                    key={category.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={isSelected} />
                        <span className={`text-sm ${isSelected ? 'text-blue-700 font-medium' : 'text-slate-600'}`}>
                          {category.name}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs">{category.count} veículos</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tag Selection (if individual access) */}
      {formAccessType === 'individual' && (
        <div className="space-y-3">
          <Label>Tags Permitidas</Label>
          {availableTags.length === 0 ? (
            <p className="text-sm text-slate-500 rounded-lg border border-slate-200 bg-slate-50 p-3">
              Nenhuma tag/recurso disponível em /resources.
            </p>
          ) : (
            <div className="max-h-[200px] overflow-y-auto border rounded-lg">
              {availableTags.map(tag => {
                const isSelected = formTags.includes(tag.id);
                const tagCode = tag.tagCode || tag.id;
                return (
                  <div
                    key={tag.id}
                    className={`p-3 border-b last:border-b-0 cursor-pointer transition-all ${isSelected ? 'bg-amber-50' : 'hover:bg-slate-50'}`}
                    onClick={() => toggleTag(tag.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={isSelected} />
                        <div>
                          <div className="font-mono font-bold text-sm">
                            <span className="bg-purple-600 text-white px-1 py-0.5 rounded-l text-xs">{tagCode.substring(0, 2)}</span>
                            <span className="bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded-r border border-l-0 border-purple-300 text-xs">{tagCode.substring(2)}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">{tag.vehicle}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">{tag.category}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-slate-900 text-3xl font-secondary font-bold tracking-tight">Gestão de Permissões</h1>
            <p className="text-slate-600 font-primary mt-1">Controlar acessos e permissões de gestores</p>
          </div>
          {canCreatePermissions && (
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleOpenCreate}
              disabled={pageLoading || submitting}
            >
              <Plus size={18} className="mr-2" />
              Adicionar Gestor
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-slate-600 text-sm font-medium">Total Gestores</p>
              <Users className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-slate-900">{totalManagers}</p>
          </Card>

          <Card className="p-5 border-emerald-200 bg-emerald-50/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-emerald-700 text-sm font-medium">Acesso Total</p>
              <ShieldCheck className="text-emerald-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-emerald-900">{totalAccessManagers}</p>
          </Card>

          <Card className="p-5 border-blue-200 bg-blue-50/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-blue-700 text-sm font-medium">Por Categoria</p>
              <Shield className="text-blue-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-blue-900">{categoryAccessManagers}</p>
          </Card>

          <Card className="p-5 border-amber-200 bg-amber-50/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-amber-700 text-sm font-medium">Individual</p>
              <ShieldAlert className="text-amber-600" size={20} />
            </div>
            <p className="text-2xl font-secondary font-bold text-amber-900">{individualAccessManagers}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <Input 
                placeholder="Pesquisar por nome, email ou cargo..." 
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterAccessType} onValueChange={setFilterAccessType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo de Acesso" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Tipos</SelectItem>
                <SelectItem value="total">Acesso Total</SelectItem>
                <SelectItem value="category">Por Categoria</SelectItem>
                <SelectItem value="individual">Individual</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>

        {/* Managers Table */}
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Gestor</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Tipo de Acesso</TableHead>
                <TableHead>Permissões</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Último Acesso</TableHead>
                {canUsePermissionActions && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(pageLoading || pageError || filteredManagers.length === 0) && (
                <TableRow>
                  <TableCell colSpan={canUsePermissionActions ? 7 : 6} className="py-12 text-center text-slate-500">
                    <Shield className="mx-auto mb-3 text-slate-300" size={48} />
                    <p className="font-medium">
                      {pageLoading ? 'A carregar permissões...' : pageError || 'Nenhum gestor encontrado'}
                    </p>
                  </TableCell>
                </TableRow>
              )}
              {filteredManagers.map((manager) => {
                const AccessIcon = accessTypeConfig[manager.accessType]?.icon || Shield;
                return (
                  <TableRow key={manager.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                          <span className="text-white font-bold">
                            {manager.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{manager.name}</p>
                          <p className="text-xs text-slate-500">{manager.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{manager.role}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${accessTypeConfig[manager.accessType]?.color} border`}>
                        <AccessIcon size={12} className="mr-1" />
                        {accessTypeConfig[manager.accessType]?.label}
                      </Badge>
                      {manager.accessType === 'category' && manager.categories.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {manager.categories.map(c => availableCategories.find(ac => ac.id === c)?.name).join(', ')}
                        </p>
                      )}
                      {manager.accessType === 'individual' && manager.tags.length > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          {manager.tags.length} tag{manager.tags.length > 1 ? 's' : ''}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1 max-w-[260px]">
                        {(manager.permissionKeys || []).length === 0 ? (
                          <span className="text-xs text-slate-400">Sem permissões</span>
                        ) : (
                          (manager.permissionKeys || []).slice(0, 3).map(permissionKey => {
                            const option = permissionOptions.find(permission => permission.id === permissionKey);
                            return (
                              <Badge key={permissionKey} variant="outline" className="text-xs bg-blue-50 text-blue-700">
                                {option?.label || humanizePermissionName(permissionKey)}
                              </Badge>
                            );
                          })
                        )}
                        {(manager.permissionKeys || []).length > 3 && (
                          <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600">
                            +{manager.permissionKeys.length - 3}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-slate-600">{manager.permissionKeys?.length || 0}</span>
                        <span className="text-xs text-slate-400">permissões</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">{manager.lastAccess}</span>
                    </TableCell>
                    {canUsePermissionActions && (
                      <TableCell>
                        <div className="flex gap-1">
                          {canUpdatePermissions && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(manager)}
                              disabled={submitting}
                            >
                              <Edit size={14} />
                            </Button>
                          )}
                          {canDeletePermissions && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleOpenDelete(manager)}
                              disabled={submitting}
                            >
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="text-blue-600" size={20} />
                Adicionar Novo Gestor
              </DialogTitle>
              <DialogDescription>
                Configurar permissões e acessos para o novo gestor
              </DialogDescription>
            </DialogHeader>
            
            <PermissionForm isEdit={false} />

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate} disabled={submitting || !canCreatePermissions}>
                {submitting ? 'A guardar...' : 'Adicionar Gestor'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="text-blue-600" size={20} />
                Editar Permissões
              </DialogTitle>
              <DialogDescription>
                Atualizar permissões de {selectedManager?.name}
              </DialogDescription>
            </DialogHeader>
            
            {/* Manager Info */}
            {selectedManager && (
              <div className="p-4 bg-slate-50 rounded-lg mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-white font-bold">
                      {selectedManager.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{selectedManager.name}</p>
                    <p className="text-sm text-slate-600">{selectedManager.email}</p>
                  </div>
                </div>
              </div>
            )}
            
            <PermissionForm isEdit={true} />

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleUpdate} disabled={submitting || !canUpdatePermissions}>
                {submitting ? 'A guardar...' : 'Guardar Alterações'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 size={20} />
                Remover Gestor
              </DialogTitle>
              <DialogDescription>
                Tem a certeza que deseja remover este gestor do sistema?
              </DialogDescription>
            </DialogHeader>

            {selectedManager && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                    <span className="text-white font-bold">
                      {selectedManager.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-red-900">{selectedManager.name}</p>
                    <p className="text-sm text-red-700">{selectedManager.email}</p>
                    <p className="text-xs text-red-600 mt-1">{selectedManager.role}</p>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)} disabled={submitting}>Cancelar</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleDelete} disabled={submitting || !canDeletePermissions}>
                {submitting ? 'A remover...' : 'Confirmar Remoção'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
